
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type StudentData = {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  has_schedule_access?: boolean;
  physical_assessments?: any[];
  training_sessions?: any[];
  fitness_level?: string;
  goals?: string;
  preferred_training_type?: string;
  upcomingEvents?: any[];
  progressData?: any[];
  weightData?: any[];
  strengthData?: any[];
  lastAssessment?: any;
  completedSessions?: number;
  nextSession?: any;
  trainer?: any;
  [key: string]: any;
}

export const useStudentData = (userId: string | undefined, userEmail?: string) => {
  return useQuery({
    queryKey: ["student-dashboard-data", userId, userEmail],
    queryFn: async () => {
      if (!userId && !userEmail) {
        console.log('No userId or userEmail provided');
        return null;
      }
      
      console.log('Fetching student data - userId:', userId, 'userEmail:', userEmail);
      
      // Buscar primeiro por auth_id, depois por email se necessário
      let student = null;
      let error = null;

      // Primeira tentativa: buscar por auth_id (estudantes registados)
      if (userId) {
        const { data: authIdResult, error: authIdError } = await supabase
          .from("students")
          .select(`
            *,
            physical_assessments (
              *
            ),
            student_progress (
              *
            ),
            training_sessions (
              created_at,
              status,
              id,
              created_by_type,
              workout:workouts (
                title
              ),
              training_session_exercises (
                id,
                weight,
                sets_completed,
                is_cardio,
                training_session_exercise_sets (
                  weight,
                  time,
                  reps
                )
              )
            )
          `)
          .eq("auth_id", userId)
          .eq("status", "active")
          .maybeSingle();

        if (authIdResult) {
          student = authIdResult;
          console.log('Student found by auth_id:', student);
        } else {
          error = authIdError;
        }
      }

      // Segunda tentativa: buscar por email se não encontrou por auth_id
      if (!student && userEmail) {
        console.log('Student not found by auth_id, trying email:', userEmail);
        const { data: emailResult, error: emailError } = await supabase
          .from("students")
          .select(`
            *,
            physical_assessments (
              *
            ),
            student_progress (
              *
            ),
            training_sessions (
              created_at,
              status,
              id,
              created_by_type,
              workout:workouts (
                title
              ),
              training_session_exercises (
                id,
                weight,
                sets_completed,
                is_cardio,
                training_session_exercise_sets (
                  weight,
                  time,
                  reps
                )
              )
            )
          `)
          .eq("email", userEmail)
          .eq("status", "active")
          .maybeSingle();
          
        student = emailResult;
        error = emailError;
        
        if (student) {
          console.log('Student found by email:', student);
        }
      }

      if (error) {
        console.error("Error fetching student data:", error);
        throw error;
      }

      if (!student) {
        console.log("No student found with auth_id:", userId, "or email:", userEmail);
        return null;
      }

      // Buscar informações do trainer
      const { data: trainerProfile, error: trainerError } = await supabase
        .from("profiles")
        .select(`
          first_name,
          last_name,
          avatar_url
        `)
        .eq("id", student.trainer_id)
        .maybeSingle();

      if (trainerError) {
        console.error("Error fetching trainer data:", trainerError);
      }

      // Buscar eventos próximos
      const { data: upcomingEvents, error: eventsError } = await supabase
        .from("schedule_events")
        .select(`
          *,
          trainer_profile:profiles!trainer_id(
            first_name,
            last_name
          )
        `)
        .eq("student_id", student.id)
        .eq("status", "scheduled")
        .gt("start_time", new Date().toISOString())
        .order('start_time', { ascending: true })
        .limit(5);

      if (eventsError) {
        console.error("Error fetching upcoming events:", eventsError);
      }

      // Formatar dados de peso das avaliações físicas
      const weightData = student.physical_assessments
        ?.sort((a: any, b: any) => new Date(a.assessment_date).getTime() - new Date(b.assessment_date).getTime())
        ?.map((assessment: any) => ({
          date: assessment.assessment_date,
          weight: assessment.weight
        })) || [];

      // Formatar dados de progresso
      const progressData = student.student_progress
        ?.sort((a: any, b: any) => new Date(a.progress_date).getTime() - new Date(b.progress_date).getTime())
        ?.map((progress: any) => ({
          date: progress.progress_date,
          value: progress.training_consistency || 0
        })) || [];
      
      const sortedAssessments = student?.physical_assessments
        ? [...student.physical_assessments].sort((a, b) => 
            new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime()
          )
        : [];
      
      const lastAssessment = sortedAssessments.length > 0 ? sortedAssessments[0] : null;
      
      // Contar sessões completas (criadas tanto por trainers como por estudantes)
      const completedSessions = student?.training_sessions?.filter(
        (session) => session.status === "completed"
      )?.length || 0;
      
      const sortedSessions = student?.training_sessions
        ? [...student.training_sessions].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        : [];
      
      const nextSession = sortedSessions.length > 0 ? sortedSessions[0] : null;

      // Processar dados das sessões de treino para incluir informações de peso
      const trainingSessionsData = student?.training_sessions
        ?.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        ?.map((session: any) => {
          // Calcular peso total levantado nesta sessão (excluindo exercícios cardio)
          let totalWeight = 0;
          let maxWeight = 0;
          let totalSets = 0;
          
          session.training_session_exercises?.forEach((exercise: any) => {
            // Ignorar exercícios cardio para cálculos de peso
            if (exercise.is_cardio) return;
            
            // Contar sets totais completos
            totalSets += exercise.sets_completed || 0;
            
            // Processar cada set para calcular peso total e máximo
            exercise.training_session_exercise_sets?.forEach((set: any) => {
              const weight = set.weight ? parseFloat(set.weight) : 0;
              const reps = set.reps ? parseInt(set.reps, 10) : 0;
              
              if (weight > 0) {
                // Calcular volume (peso x repetições) para este set
                const setVolume = weight * reps;
                totalWeight += setVolume;
                
                // Acompanhar peso máximo
                if (weight > maxWeight) {
                  maxWeight = weight;
                }
              }
            });
          });

          return {
            date: session.created_at,
            pesoMáximo: maxWeight,
            pesoTotal: totalWeight,
            séries: totalSets,
            createdBy: session.created_by_type
          };
        }) || [];
      
      // Criar dados específicos de progresso de força para gráficos
      const strengthData = trainingSessionsData.map(session => ({
        date: session.date,
        totalWeight: session.pesoTotal,
        maxWeight: session.pesoMáximo,
        sets: session.séries,
        createdBy: session.createdBy
      }));

      console.log("Student data processed successfully");
      console.log("Physical assessments:", student.physical_assessments?.length || 0);
      console.log("Student progress records:", student.student_progress?.length || 0);
      console.log("Training sessions:", student.training_sessions?.length || 0);
      console.log("Completed sessions:", completedSessions);
      console.log("Weight data points:", weightData.length);
      console.log("Progress data points:", progressData.length);
      console.log("Strength data points:", strengthData.length);
      
      return {
        ...(student as StudentData),
        upcomingEvents: upcomingEvents || [],
        progressData,
        weightData,
        lastAssessment,
        completedSessions,
        nextSession,
        trainer: trainerProfile || null,
        trainingSessionsData,
        strengthData
      };
    },
    enabled: !!(userId || userEmail),
    staleTime: 300000
  });
};
